import { useRef, useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { FileText, Scale, Mic, CheckCircle, BadgeCheck } from "lucide-react";

function ServiceCard({ children, index, isVisible, className = "" }) {
    return (
        <div
            className={className}
            style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(32px)",
                transition: `opacity 0.6s ease ${index * 0.15}s, transform 0.6s ease ${index * 0.15}s`,
            }}
        >
            {children}
        </div>
    );
}

export default function Services() {
    const { t } = useLanguage();
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setIsVisible(true);
            },
            { threshold: 0.1 }
        );
        const current = sectionRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, []);

    return (
        <section id="servicios" className="py-20 md:py-32 px-6" ref={sectionRef} data-testid="services-section">
            <div className="max-w-7xl mx-auto">
                <div
                    className="text-center mb-16"
                    style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? "translateY(0)" : "translateY(24px)",
                        transition: "opacity 0.6s ease, transform 0.6s ease",
                    }}
                >
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                        {t.services.title}
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                        {t.services.subtitle}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <ServiceCard index={0} isVisible={isVisible} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 hover:border-blue-400/50 dark:hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
                            <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3" data-testid="service-simple-title">
                            {t.services.simple.title}
                        </h3>
                        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                            {t.services.simple.description}
                        </p>
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                            {t.services.simple.items.map((item, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </ServiceCard>

                    <ServiceCard index={1} isVisible={isVisible} className="bg-white dark:bg-slate-900 border-2 border-blue-500 rounded-xl p-8 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                        <div className="absolute -top-3 left-6">
                            <span className="bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
                                {t.services.sworn.includes}
                            </span>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
                            <Scale className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3" data-testid="service-sworn-title">
                            {t.services.sworn.title}
                        </h3>
                        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                            {t.services.sworn.description}
                        </p>
                        <div className="space-y-2 mb-6">
                            {t.services.sworn.items.map((item, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
                            {t.services.sworn.features.map((feat, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                                    <BadgeCheck className="w-4 h-4" />
                                    <span>{feat}</span>
                                </div>
                            ))}
                        </div>
                    </ServiceCard>

                    <ServiceCard index={2} isVisible={isVisible} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 hover:border-blue-400/50 dark:hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
                            <Mic className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3" data-testid="service-interpretation-title">
                            {t.services.interpretation.title}
                        </h3>
                        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                            {t.services.interpretation.description}
                        </p>
                        <div className="space-y-2">
                            {t.services.interpretation.items.map((item, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </ServiceCard>
                </div>
            </div>
        </section>
    );
}
